// ============================================================================
// Storage Constants
// ============================================================================

/** Default maximum age for cached data (7 days in milliseconds) */
export const DEFAULT_CACHE_MAX_AGE = 7 * 24 * 60 * 60 * 1000

/** Default maximum items per resource type */
export const DEFAULT_MAX_ITEMS = 1000

/** IndexedDB database name */
export const IDB_DATABASE_NAME = 'portal-offline'

/** Storage key prefixes */
export const STORAGE_PREFIXES = {
  QUERY_CACHE: 'query',
  MUTATION_QUEUE: 'mutation',
  CONFLICT: 'conflict',
  METADATA: 'meta',
  BLOB_STORE: 'blob',
} as const

// ============================================================================
// Sync Constants
// ============================================================================

/** Default sync interval in milliseconds (30 seconds) */
export const DEFAULT_SYNC_INTERVAL = 30 * 1000

/** Default maximum retry attempts */
export const DEFAULT_MAX_RETRIES = 3

/** Default base delay for exponential backoff (1 second) */
export const DEFAULT_RETRY_BASE_DELAY = 1000

/** Default maximum delay for exponential backoff (30 seconds) */
export const DEFAULT_RETRY_MAX_DELAY = 30 * 1000

/** Default batch size for processing mutations */
export const DEFAULT_BATCH_SIZE = 5

/** Debounce delay for persisting query cache (500ms) */
export const PERSIST_DEBOUNCE_DELAY = 500

// ============================================================================
// Query Constants
// ============================================================================

/** Default stale time for offline data (5 minutes) */
export const DEFAULT_OFFLINE_STALE_TIME = 5 * 60 * 1000

/** Default garbage collection interval (1 hour) */
export const DEFAULT_GC_INTERVAL = 60 * 60 * 1000

// ============================================================================
// Network Constants
// ============================================================================

/** Debounce delay for network status changes (100ms) */
export const NETWORK_DEBOUNCE_DELAY = 100

/** Timeout for network connectivity check (5 seconds) */
export const NETWORK_CHECK_TIMEOUT = 5000

// ============================================================================
// UI Constants
// ============================================================================

/** Animation duration for offline banner (300ms) */
export const BANNER_ANIMATION_DURATION = 300

/** Auto-dismiss delay for success notifications (3 seconds) */
export const NOTIFICATION_AUTO_DISMISS = 3000

// ============================================================================
// HTTP Status Codes
// ============================================================================

export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  TOO_MANY_REQUESTS: 429,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
} as const

/** Status codes that should trigger a retry */
export const RETRYABLE_STATUS_CODES = [
  HTTP_STATUS.TOO_MANY_REQUESTS,
  HTTP_STATUS.INTERNAL_SERVER_ERROR,
  HTTP_STATUS.SERVICE_UNAVAILABLE,
] as const

// ============================================================================
// Error Messages
// ============================================================================

export const ERROR_MESSAGES = {
  NETWORK_OFFLINE: 'offline.errors.network_offline',
  SYNC_FAILED: 'offline.errors.sync_failed',
  CONFLICT_DETECTED: 'offline.errors.conflict_detected',
  STORAGE_FULL: 'offline.errors.storage_full',
  INVALID_DATA: 'offline.errors.invalid_data',
  MUTATION_FAILED: 'offline.errors.mutation_failed',
  QUEUE_FULL: 'offline.errors.queue_full',
} as const
